import { computed, ComputedRef, isRef, ref } from "vue"

import { logError, logWarning } from "@Helpers/logger.js";
import useNotify from "@Composables/useNotify.js";
import { ReactivePaginator } from "@Resources/js/api/ReactivePaginator.ts";
import { FetchUrlOptions, LastFetchState, UpdateUrlOptions } from "@Modules/urls/Api/UrlsApi.js";
import { buildFilterQuery } from "@Modules/urls/Helpers/buildFilterQuery";
import { UrlAlias } from "@Modules/urls/Data/Models/UrlAliasFactory.ts";
import { XUrlsStore } from "@Modules/urls/Store/XUrlsStore.ts";
import useTeamCan from "@Modules/teams/Auth/useTeamCan.ts";
import type { Organization } from "@Modules/teams/Data/Models/OrganizationModel.ts";
import type { Team } from "@Modules/teams/Data/Models/TeamModel.ts";
import ReplaceableRouter from "@Modules/shared/interfaces/ReplaceableRouter.ts";
import VisitOrganizationOrTeam from "@Modules/teams/Api/Visitors/VisitOrganizationOrTeam.ts";
import UpdateBrowserQuery from "@Modules/teams/Api/Visitors/UpdateBrowserQuery.ts";

export type FetchOptions = {
    filter?: NonNullable<FetchUrlOptions["filter"]>["type"];
    searchType?: NonNullable<FetchUrlOptions["search"]>["searchType"];
    search?: string,
    clear?: boolean,
    includeAlias?: string | null,
};

export interface TeamUrlViewModel extends ReplaceableRouter<TeamUrlViewModel, "get" | "post" | "delete" | "patch"> {
    canViewUrls: ComputedRef<boolean>,
    paginator: ReactivePaginator<UrlAlias>,
    loading: ComputedRef<boolean>,
    sending: ComputedRef<boolean>,
    errors: ComputedRef<Record<string, string>>,
    lastFetch: ComputedRef<LastFetchState>,
    fetch: (options: FetchOptions) => Promise<void>,
    downloadUrls: () => void,
    clearErrors: () => void,
    getUrlAlias: (slug?: string) => UrlAlias | null,
    deleteUrl: (urlAlias: UrlAlias) => Promise<boolean>,
    archiveUrl: (urlAlias: UrlAlias) => Promise<boolean>,
    updateUrl: (options: UpdateUrlOptions) => Promise<boolean>,
    setStatsTracking:(urlAlias: UrlAlias, enabled: boolean) => Promise<boolean>,
}

export default function TeamUrlViewModel({
    org = ref<Organization | null>(null),
    team = ref<Team | null>(null),
}): TeamUrlViewModel {

    if (!isRef(org)) {
      logError("team url view model org is not a ref");
    }

    if (!isRef(team)) {
      logError("team url view model team is not a ref");
    }

    const urlStore = XUrlsStore();
    const { notifySuccess, notifyError } = useNotify();

    /**
     * Returns true if we are allowed to view the urls for the provided org and team
     */
    const canViewUrls = computed<boolean>(() => useTeamCan({ org, team }).can("view_urls"));

    function syncUrlWithFilters(preserveAlias?: string | null): void {
        const query = buildFilterQuery(urlStore.lastFetch);

        if (preserveAlias) {
            query.alias = preserveAlias;
        }

        /** the filters own these keys, so any they no longer set is dropped rather than left stale */
        UpdateBrowserQuery({ query, remove: ["list", "search", "searchType", "tag", "alias"] });
    }

    const thisClass = {
        canViewUrls,
        paginator: urlStore.paginator,
        sending: computed(() => urlStore.sending),
        loading: computed(() => urlStore.loading),
        errors: computed(() => urlStore.errors),
        lastFetch: computed(() => urlStore.lastFetch),
        clearErrors: urlStore.clearErrors,

        getUrlAlias: (slug?: string): UrlAlias | null => {
            if (!slug) {
                return null;
            }

            const urls = urlStore.paginator.page?.data ?? [];

            return urls.find((urlAlias: UrlAlias) => {
                const main = urlAlias.mainAlias();
                if (!main) {
                    return false;
                }
                return `${main.domain}/${main.alias}` === slug;
            }) ?? null;
        },

        /**
         * Triggers a download of URLs in CSV format by navigating to the download URL.
         * Does nothing if URLs are still loading or if there are no URLs to download.
         */
        downloadUrls: (): void => {
            if (urlStore.loading) {
                return;
            }

            if (urlStore.urls.length === 0) {
                return;
            }

            const url = urlStore.downloadUrl;
            if (url) {
                window.location.href = url;
            }
        },

        /*
         * Replace the http router with another implementation
         */
        setRouter: (router: Pick<HttpInterface, "get" | "post" | "delete" | "patch">) => {
            urlStore.setRouter(router);
            return thisClass;
        },

        /**
         * Fetches a list of urls for an organization or workspace
         * @param Boolean clear If true we clear all urls before performing the search
         *                      If false we append the urls to the end of the list of urls
         */
        fetch: async function ({
            filter = "available",
            searchType = "alias",
            search = "",
            clear = false,
            includeAlias,
        }: Partial<FetchOptions>,
        ): Promise<void> {
            const orgID = org.value?.slug;
            const teamID = team.value?.slug || null;

            if (!orgID) {
              logError("no organization provided. cannot fetch team urls");
              return;
            }

            if (canViewUrls.value || teamID === "personal") {
                await urlStore.fetchTeamUrls({
                    orgID,
                    teamID,
                    filter: { "type": filter },
                    search: { searchType, searchValue: search },
                    clear,
                    includeAlias,
                });

                syncUrlWithFilters(includeAlias);
            } else {
              logWarning("could not fetch urls (no permisson)");
              urlStore.clearUrls();
            }
        },

        /**
         * Deletes a URL from the server and removes it from the paginator
         */
        deleteUrl: async function (urlAlias: UrlAlias): Promise<boolean> {
            const orgID = org.value?.slug;
            const teamID = team.value?.slug || null;

            if (!orgID) {
                console.error("no organization provided. cannot delete url");
                return false;
            }

            const mainAlias = urlAlias.mainAlias();
            if (!mainAlias) {
                console.error("no main alias found. cannot delete url");
                return false;
            }

            const success = await urlStore.deleteUrl({
                orgSlug: orgID,
                teamSlug: teamID,
                domain: mainAlias.domain,
                alias: mainAlias.alias,
            });

            if (success) {
                notifySuccess({ text: "The TinyURL has been deleted." });

                if (team.value) {
                    team.value.visit();
                } else if (org.value) {
                    org.value.visit();
                }
            } else {
                notifyError({ text: "Failed to delete the TinyURL." });
            }

            return success;
        },

        /**
         * Toggles the archived state of a URL
         */
        archiveUrl: async function (urlAlias: UrlAlias): Promise<boolean> {
            const orgID = org.value?.slug;
            const teamID = team.value?.slug || null;

            if (!orgID) {
                logError("no organization provided. cannot archive url");
                return false;
            }

            const mainAlias = urlAlias.mainAlias();
            if (!mainAlias) {
                logError("no main alias found. cannot archive url");
                return false;
            }

            const wasArchived = mainAlias.is_archived;
            const success = await urlStore.archiveUrl({
                orgSlug: orgID,
                teamSlug: teamID,
                domain: mainAlias.domain,
                alias: mainAlias.alias,
            });

            if (success) {
                const action = wasArchived ? "unarchived" : "archived";
                notifySuccess({ text: `The TinyURL has been ${action}.` });
            } else {
                const action = wasArchived ? "unarchive" : "archive";
                notifyError({ text: `Failed to ${action} the TinyURL.` });
            }

            return success;
        },

        /**
         * Updates an existing URL
         */
        updateUrl: async function (options: UpdateUrlOptions): Promise<boolean> {
            const success = await urlStore.updateUrl(options);

            if (success) {
                notifySuccess({ text: "The TinyURL has been updated." });

                VisitOrganizationOrTeam({
                    orgSlug: org.value?.slug ?? "",
                    teamSlug: team.value?.slug ?? null,
                    alias: `${options.domain}/${options.alias || options.originalAlias}`,
                });
            } else {
                notifyError({ text: "Failed to update the TinyURL." });
            }

            return success;
        },

        setStatsTracking: async function (urlAlias: UrlAlias, enabled: boolean): Promise<boolean> {
          const orgID = org.value?.slug;
          const teamID = team.value?.slug || null;

          if (!orgID) {
            logError("no organization provided. cannot archive url");
            return false;
          }

          const mainAlias = urlAlias.mainAlias();
          if (!mainAlias) {
            logError("no main alias found. cannot archive url");
            return false;
          }
          
          const success = await urlStore.setStatsTracking({
            orgSlug: orgID,
            teamSlug: teamID,
            domain: mainAlias.domain,
            alias: mainAlias.alias,
            enabled, 
          });

          if (success) {
            notifySuccess({ text: "The TinyURL has been updated." });
          } else {
            notifyError({ text: "Failed to update the TinyURL." });
          }

          return success;
        },
    };

    return thisClass;
}
